import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Chapter, Question } from '../../models/questionnaire.model';
import { QuestionnaireService } from 'src/app/services/questionnaire.service';
import { AuthService } from 'src/app/services/auth.service';
import { cloneDeep } from 'lodash';

@Component({
    selector: 'app-questionnaire-chapters',
    templateUrl: './questionnaire-chapters.component.html',
    styleUrl: './questionnaire-chapters.component.scss',
    standalone: false
})
export class QuestionnaireChaptersComponent implements OnInit {
	@Input() report: boolean = false;
	@Input() lang_code!: string;
	@Input() chapters: Chapter[] = [];
	@Input() viewMode: boolean = false;
	@Output() chaptersRemoved = new EventEmitter<any[]>();
	@Output() questionsRemoved = new EventEmitter<any[]>();

	@ViewChild('viewQuestionModal') viewQuestionModal!: TemplateRef<any>;
	@ViewChild('submitChapterModal') submitChapterModal!: TemplateRef<any>;
	@ViewChild('deleteChapterModal') deleteChapterModal!: TemplateRef<any>;

	currentChapter!: Chapter | null;
	selectedChapter!: Chapter;
	title = ''
	chapterName = '';

	submitted = false;
	spinner = false;
	editMode = false;

	submitChapterModalRef: any;
	deleteChapterModalRef: any;

	chapterIndex!: number;
	currentChapterIndex!: number;
	questionIndex!: number;

	removeChapters: any[] = [];
	removeQuestions: any[] = [];
	themeList: any[] = [];

	selectedQuestion!: Question;
	selectedQuestionIndex!: number;

	constructor(private modalService: NgbModal, private questionnaireService: QuestionnaireService, private authService: AuthService) {	}

	ngOnInit(): void {
		this.questionnaireService.getGeneralList('questionnaire_theme').subscribe({
			next: (list) => {
				this.themeList = list.values;
			},
			error: (err) => {
				this.authService.toastErrorMessage(err.message ? err.message : err, 'Getting Questionnaire Templates')
			}
		});
	}

	getColor(value: number | undefined) {
		if (!value) {
			return '#299cdb';
		}

		if (value < 25) {
			return '#40bb82';
		} else {
			if (value < 50) {
				return '#ffca5b';
			} else {
				if (value < 85) {
					return '#f1963b';
				} else {
					return '#ee6352';
				}
			}
		}
	}

	viewQuestion(event: Event, question_index: number) {
		event.stopPropagation();

		if (this.currentChapter) {
			this.selectedQuestionIndex = question_index;
			this.selectedQuestion = this.currentChapter.questions[question_index];
	
			this.modalService.open(this.viewQuestionModal, { size: 'xl', backdrop: 'static', keyboard : false });
		}
	}

	duplicateQuestion(event: Event, question: Question) {
		event.stopPropagation();

		if (this.currentChapter) {
			const newQuestion: Question = cloneDeep(question)
	
			newQuestion.name = '';
			newQuestion.body.question = null;
	
			this.currentChapter.questions.push(newQuestion);
		}
	}

	addChapter(content: any) {
		this.title = 'Create New Chapter';
		this.modalService.open(content, { size: 'l', backdrop: 'static', keyboard : false });
	}

	editChapter(event: any, index: number) {
		event.stopPropagation();
		
		this.title = 'Edit Chapter';
		this.editMode = true;
		this.chapterIndex = index;
		this.chapterName = this.chapters[this.chapterIndex].name;
		this.submitChapterModalRef = this.modalService.open(this.submitChapterModal, { size: 'l', backdrop: 'static', keyboard : false });
	}

	saveChapter() {
		if (this.editMode) {
			this.chapters[this.chapterIndex].name = this.chapterName;
		} else {
			const newChapter: Chapter = {
				name: this.chapterName,
				display_order: 0,
				score: 0,
  			is_calc: false,
				questions: []
			}; 
		
			this.chapters.push(newChapter);
			this.currentChapterIndex = this.chapters.length-1;
			this.currentChapter = newChapter;
		}

		this.closeModal();
	}

	calcResponseTime(start: number, end: number) {
		let ms;
		try {
			ms = (end - start)/1000;
		} catch (error) {
			ms = 0;
		}

		return parseFloat(ms.toFixed(2))
	}

	calcResponseTimeMinutes(timeInSeconds: number): string {
		if (timeInSeconds >= 60) {
			const minutes = Math.floor(timeInSeconds / 60);
			const remainingSeconds = parseFloat((timeInSeconds % 60).toFixed(2));
			let result = `${minutes} minute${minutes > 1 ? 's' : ''}`;
			if (remainingSeconds > 0) {
				result += ` ${remainingSeconds} second${remainingSeconds > 1 ? 's' : ''}`;
			}
			return result;
		} else {
			return `${timeInSeconds} second${timeInSeconds !== 1 ? 's' : ''}`;
		}
	}

	openDeleteChapterModal(event: any, chapter: Chapter, index: number) {
		event.stopPropagation();
		this.chapterIndex = index;
		this.selectedChapter = chapter;
		this.deleteChapterModalRef = this.modalService.open(this.deleteChapterModal, { size: 'l', backdrop: 'static', keyboard : false });
	}

	deleteChapter() {
		if (this.selectedChapter.id) {
			const id = this.selectedChapter.id;
			this.removeChapters.push(id);
			this.chaptersRemoved.emit(this.removeChapters);
		}

		this.deleteChapterModalRef.close();
		this.chapters.splice(this.chapterIndex, 1);
		this.currentChapter = null;
	}

	changeChapter(chapter: Chapter, index: number) {
		this.currentChapterIndex = index;
		this.currentChapter = chapter;
	}

	addQuestion() {
		if (this.currentChapter) {
			const newQuestion: Question = {
				name: '',
				display_order: 0,
				time_limit: 30,
				count_down: 5,
				theme: null,
				threshold: null,
				body: {
					question: null
				},
				response: {
					response_type: null
				}
			}

			this.currentChapter.questions.push(newQuestion);
		}
	}

	openDeleteQuestionModal(event: Event, content: any, index: number) {
		event.stopPropagation();

		this.questionIndex = index;
		this.modalService.open(content, { size: 'l', backdrop: 'static', keyboard : false });
	}

	deleteQuestion() {
		if (this.currentChapter) {
			if (this.currentChapter.questions[this.questionIndex].id) {
				const id = this.currentChapter.questions[this.questionIndex].id;
				this.removeQuestions.push(id);
				this.questionsRemoved.emit(this.removeQuestions);
			}
	
			this.currentChapter.questions.splice(this.questionIndex, 1);
			this.modalService.dismissAll();
		}
	}

	closeModal() {
		this.editMode = false;
		this.chapterName = '';
		this.modalService.dismissAll();
	}
}
